<template>
  <div class="restaurant-detailed-view min-h-screen bg-background/50 dark:bg-background/80">
    <BubbleHeader :bubble="bubble" @update:bubble="handleBubbleUpdate" />

    <div class="max-w-7xl mx-auto p-4 sm:p-6 space-y-8">
      <Card class="mb-6 overflow-hidden">
        <CardContent class="p-0">
          <div class="relative aspect-[21/9] w-full">
            <NuxtImg
              :src="getCoverPhoto ?? undefined"
              :alt="bubble.title"
              class="absolute inset-0 w-full h-full object-cover transition-transform duration-300 hover:scale-105"
              loading="eager"
              placeholder
              sizes="(max-width: 768px) 100vw, 1280px"
              format="webp"
              quality="90"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <div class="absolute bottom-0 left-0 right-0 p-6">
              <h1 class="text-3xl font-bold text-white drop-shadow-lg">{{ bubble.title }}</h1>
            </div>
          </div>
        </CardContent>
      </Card>

      <TypeChips :primary-type="bubble.primaryType" :types="bubble.types" class="mb-6" />

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Details
          :address="bubble.address"
          :phone-number="bubble.phoneNumber"
          :website="bubble.website"
          :price-range="bubble.priceRange"
          :rating="bubble.rating"
          :rating-count="bubble.ratingCount"
        />

        <Hours :hours="bubble.hoursOfOperation" />
      </div>

      <Description v-if="bubble.description" :description="bubble.description" />

      <Gallery :photos="getPhotos" :title="bubble.title" />

      <Card v-if="bubble.favoriteDishes && bubble.favoriteDishes.length > 0" class="mb-6">
        <CardHeader>
          <CardTitle>Favorite Dishes</CardTitle>
        </CardHeader>
        <CardContent>
          <ul class="list-disc list-inside">
            <li v-for="dish in bubble.favoriteDishes" :key="dish">
              {{ dish }}
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card v-if="bubble.dietaryOptions && bubble.dietaryOptions.length > 0" class="mb-6">
        <CardHeader>
          <CardTitle>Dietary Options</CardTitle>
        </CardHeader>
        <CardContent>
          <ul class="list-disc list-inside">
            <li v-for="option in bubble.dietaryOptions" :key="option">
              {{ option }}
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card v-if="bubble.specialFeatures" class="mb-6">
        <CardHeader>
          <CardTitle>Special Features</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{{ bubble.specialFeatures }}</p>
        </CardContent>
      </Card>

      <ActionButtons
        :website="bubble.website"
        :location="bubble.location"
        :menu-url="bubble.menuUrl ?? undefined"
        :reservation-url="bubble.reservationUrl ?? undefined"
      />

      <!-- Stats Section -->
      <div
        class="bg-card dark:bg-card/50 rounded-xl shadow-lg transition-all duration-200 hover:shadow-xl"
      >
        <div class="p-6 border-b border-border dark:border-border/50">
          <h2 class="text-xl font-semibold flex items-center gap-2">
            <Icon name="ph:chart-bar" class="w-5 h-5 text-primary" />
            Your Stats
          </h2>
        </div>
        <BubbleStats
          :bubble-uuid="bubble.uuid"
          did-label="Visited"
          placeholder="Write your thoughts about this restaurant..."
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { RestaurantBubble, Bubble, AnyBubble } from '@/stores/interfaces'
import { useMediaResources } from '@/composables/useMediaResources'
import BubbleHeader from '../BubbleHeader.vue'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import Details from '../specific/places/Details.vue'
import Description from '../specific/places/Description.vue'
import Gallery from '../specific/places/Gallery.vue'
import TypeChips from '../specific/places/TypeChips.vue'
import ActionButtons from '../specific/places/ActionButtons.vue'
import Hours from '../specific/places/Hours.vue'
import BubbleStats from '../BubbleStats.vue'
import { useTypeNavigation } from '@/composables/useTypeNavigation'

const props = defineProps<{
  bubble: RestaurantBubble
}>()

const localBubble = ref(props.bubble)
const { getCoverPhoto, getPhotos } = useMediaResources(props.bubble)

// Use the type navigation composable
useTypeNavigation(props.bubble)

const handleBubbleUpdate = (updatedBubble: Bubble | AnyBubble) => {
  if (
    'primaryType' in updatedBubble &&
    'types' in updatedBubble &&
    'favoriteDishes' in updatedBubble
  ) {
    localBubble.value = updatedBubble as RestaurantBubble
  }
}
</script>
